// The deployment's white-label identity, as the SPA sees it.
//
// Read from the `#pms-brand` script tag that `internal/platform/brand` rewrites
// while serving index.html — NOT fetched. A fetch would mean the sign-in screen,
// which is the first thing painted and the most prominent place the product
// name appears, renders with the wrong one until the response lands.
//
// The name is deliberately NOT an i18n key. `app.name` used to be one and read
// "Hotel PMS" in both `en` and `id`, which is the tell: a product name is a
// proper noun, not a translatable string, and putting it in the locale files
// meant a reseller had to edit two of them and rebuild. It is configuration now
// (`PMS_BRAND_NAME`), so CLAUDE.md §8's "no user-facing string literals" is not
// in play — there is no literal here to translate.

import { applyUiFlags, type DeployUi } from './uiFlag'
import { isKioskURL } from '../features/kiosk/entry'
import { isBookingURL } from '../features/publicbooking/entry'
import { isRoomOrderURL } from '../features/roomorder/entry'

export type Brand = {
  name: string
  /** Accent on light surfaces. */
  accent: string
  /** Accent on gray 100 and darker. Separate because deriving it is a guess. */
  accentDark: string
  /** URL of an operator-supplied mark, or '' for the built-in one. */
  mark: string
  /** The deployment's default interface (PMS_UI). Refined unless told otherwise. */
  ui: DeployUi
  // The three below exist only under a preset (PMS_BRAND_PRESET). They are
  // OPTIONAL and absent for the default product, whose payload is unchanged.
  /** Preset name, e.g. 'indoxyz'. Becomes `data-brand-preset` on <html>. */
  preset?: string
  /** The mark's SVG viewBox, so a non-square mark is sized by its real shape. */
  markViewBox?: string
  /** The mark is one colour and may be painted with the text colour. */
  markMono?: boolean
}

// What a `vite dev` server, a vitest run and a binary with no PMS_BRAND_* all
// fall back to. Keep in step with `brand.Default()` in Go — `brand.test.ts`
// pins this against index.html, and index.html against the Go anchors.
export const DEFAULT_BRAND: Brand = {
  name: 'Valid PMS',
  // The LIGHT-surface accent is $brand-dim, not $brand: the raw brand is 1.53:1
  // on paper, which is what the boot guard in internal/platform/brand now warns
  // about. The dark one is the brand itself, at 11.8:1 on the ink chrome.
  accent: '#7f7401',
  accentDark: '#e7d301',
  mark: '',
  ui: 'refined',
}

export const BRAND_ELEMENT_ID = 'pms-brand'

/**
 * Parse the injected brand, falling back field by field.
 *
 * Field by field rather than all-or-nothing: a future server that adds a key
 * this build does not know about, or omits one it does, must not blank the
 * product name. A logo screen is not worth failing a shift over.
 */
export function readBrand(doc?: Document): Brand {
  // `document` is resolved lazily rather than as a default parameter, because
  // this module evaluates `readBrand()` at import time and a default parameter
  // would touch `document` there. Anything without a DOM — a node-environment
  // test, a script, an SSR pass — would then fail on importing anything that
  // imports the Logo. That is not hypothetical: it is how this was found.
  const d = doc ?? (typeof document === 'undefined' ? null : document)
  if (!d) return DEFAULT_BRAND
  const el = d.getElementById(BRAND_ELEMENT_ID)
  if (!el?.textContent) return DEFAULT_BRAND
  let raw: unknown
  try {
    raw = JSON.parse(el.textContent)
  } catch {
    return DEFAULT_BRAND
  }
  if (typeof raw !== 'object' || raw === null) return DEFAULT_BRAND
  const got = raw as Partial<Record<keyof Brand, unknown>>
  const str = (k: 'name' | 'accent' | 'accentDark') =>
    typeof got[k] === 'string' && got[k] ? (got[k] as string) : DEFAULT_BRAND[k]
  const out: Brand = {
    name: str('name'),
    accent: str('accent'),
    accentDark: str('accentDark'),
    // '' is a MEANINGFUL value here — it says "no override, use the built-in
    // component" — so it must survive the fallback rather than being treated as
    // missing. DEFAULT_BRAND.mark is '' too, which makes this safe either way.
    mark: typeof got.mark === 'string' ? got.mark : DEFAULT_BRAND.mark,
    ui: got.ui === 'classic' ? 'classic' : 'refined',
  }
  // Keys are added only when present, so a default payload round-trips to
  // exactly DEFAULT_BRAND.
  if (typeof got.preset === 'string' && got.preset) out.preset = got.preset
  if (typeof got.markViewBox === 'string' && got.markViewBox) out.markViewBox = got.markViewBox
  if (got.markMono === true) out.markMono = true
  return out
}

export const brand = readBrand()

/**
 * Push the brand into the document. Called once from `main.tsx`.
 *
 * The accents become CSS custom properties rather than being threaded through
 * components, so a stylesheet can react to them — `.pms-top` swaps to the dark
 * one for the whole subtree with a single declaration.
 */
export function applyBrand(b: Brand = brand, doc: Document = document): void {
  doc.documentElement.style.setProperty('--pms-brand-accent', b.accent)
  doc.documentElement.style.setProperty('--pms-brand-accent-dark', b.accentDark)
  // The switch `styles/presets.scss` keys on. Absent for the default product.
  if (b.preset) doc.documentElement.setAttribute('data-brand-preset', b.preset)
  else doc.documentElement.removeAttribute('data-brand-preset')
  // The "Refined" design option (`?ui=refined`, `?density=compact`) is a STAFF
  // option: the three guest surfaces never carry the attributes, so nothing under
  // `[data-ui='refined']` can reach them even by accident.
  const path = doc.defaultView?.location.pathname ?? '/'
  const guest = isKioskURL(path) || isBookingURL(path) || isRoomOrderURL(path)
  if (!guest) applyUiFlags(doc, b.ui)
  // The binary sets <title> too. This covers the case where the marker was
  // injected but the title anchor was not — Inject reports that as a warning
  // and serves anyway, so the tab would otherwise keep the vendor's name.
  if (doc.title !== b.name) doc.title = b.name
}
